import { useInfiniteQuery } from "@tanstack/react-query";
import {
  cn,
  ControlCodes,
  LegacyPartnerEndpoints,
  displayPlatform,
  getEnumName,
  pluralize,
  queries,
  useAuthConfig,
  useDebounceState,
  usePartnerEndpointDeploy,
  useRecentEndpointQuery,
} from "@theprelude/core";
import {
  Button,
  Checkbox,
  CheckmarkIcon,
  CloseIcon,
  IconButton,
  Loading,
  SearchIcon,
  TooltipArrow,
  TooltipContent,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  PlatformIcon,
  notifyError,
  notifySuccess,
  Input,
} from "@theprelude/ds";
import { useState } from "react";
import { z } from "zod";
import { DefaultOverlay } from "../default-overlay";

const osOptions = ["windows", "linux", "darwin"];

const PAGE_SIZE = 100;

export const addPartnerProbeSchema = z.tuple([z.literal("add-partner-probe")]);

export function AddPartnerProbeOverlay() {
  const authConfig = useAuthConfig();
  const [platform, setPlatform] = useState("");
  const [view, setView] = useState<"list" | "deploy">("list");
  const [selectedEndpoints, setSelectedEndpoints] = useState<Set<string>>(
    new Set(),
  );
  const deploy = usePartnerEndpointDeploy();

  const handleDeploy = () => {
    deploy.mutate(
      {
        partnerCode: ControlCodes.CROWDSTRIKE,
        hostIds: [...selectedEndpoints],
      },
      {
        onError() {
          notifyError({
            message: "Failed to deploy endpoints. Please try again",
          });
        },
        onSuccess() {
          notifySuccess({ message: "Endpoints successfully deployed" });
          setView("deploy");
        },
      },
    );
  };

  const searchTerm = useDebounceState("");
  const query = queries.partner.endpoints(authConfig, {
    partner: getEnumName(ControlCodes, ControlCodes.CROWDSTRIKE),
    platform,
    hostname: searchTerm.debouncedValue,
    count: PAGE_SIZE,
  });

  const partnerEndpointsQuery = useInfiniteQuery({
    initialPageParam: 0,
    queryKey: query.queryKey,
    queryFn: ({ pageParam }) => {
      return query.queryFn(pageParam);
    },
    getNextPageParam: (lastPage, pages) => {
      if (Object.keys(lastPage).length < PAGE_SIZE) {
        return;
      }

      const totalCount = pages.reduce(
        (acc, page) => acc + Object.keys(page).length,
        0,
      );

      return totalCount;
    },
    enabled: platform !== "",
  });

  const pages = partnerEndpointsQuery.data?.pages ?? [];
  const partnerEndpoints: LegacyPartnerEndpoints = Object.assign({}, ...pages);

  const { data: endpointIds } = useRecentEndpointQuery(
    (endpoints) =>
      new Set(
        endpoints.map((e) =>
          (e.edr_id ?? "").toLowerCase().replaceAll("-", ""),
        ),
      ),
  );

  const edrIds = Object.keys(partnerEndpoints);

  const getDeployButtonCopy = () => {
    const selected = selectedEndpoints.size;
    let existing = 0;
    for (const e of selectedEndpoints) {
      if (endpointIds?.has(e)) existing++;
    }

    if (
      (selected > 1 && existing === 0) ||
      (selected > 1 && existing === selected)
    ) {
      return `Deploy on ${selected} selected ${pluralize(selected, "host")}`;
    }

    if (selected > 1 && existing < selected) {
      return `Deploy on ${selected} selected ${pluralize(selected, "host")} (${
        selected - existing
      } new)`;
    }

    return "Deploy";
  };

  return (
    <DefaultOverlay title={"Deploy a probe using CrowdStrike"}>
      <header>
        <div className="flex flex-col gap-6 p-4">
          <span>Select an operating system:</span>
          <div className="flex">
            {osOptions.map((osOption) => (
              <Button
                key={osOption}
                className={cn(
                  "rounded-none border border-highlight bg-primary-foreground px-2 py-3 text-primary-background hover:bg-highlight hover:text-primary-foreground",
                  osOption === platform &&
                    "bg-highlight text-primary-foreground",
                )}
                icon={<PlatformIcon platform={osOption} />}
                onClick={() => {
                  setPlatform(osOption);
                  setSelectedEndpoints(new Set());
                  searchTerm.setImmediate("");
                  setView("list");
                }}
              >
                {displayPlatform(osOption)}
              </Button>
            ))}
          </div>
          {platform !== "" && (
            <Input
              before={<SearchIcon />}
              after={
                <IconButton
                  icon={<CloseIcon />}
                  onClick={() => searchTerm.setImmediate("")}
                />
              }
              placeholder="Search for a host by name"
              value={searchTerm.value}
              onChange={(ev) => {
                searchTerm.setDelayed(ev.target.value);
                setView("list");
              }}
            />
          )}
        </div>

        <div className="border-b border-stroke-line" />

        {!partnerEndpointsQuery.isLoading && (
          <div className="flex justify-between border-b border-stroke-line p-4 text-secondary-foreground">
            {view === "list" && (
              <>
                <p>{edrIds.length} hosts</p>

                {edrIds.length > 0 &&
                  (edrIds.length === selectedEndpoints.size ? (
                    <button
                      className="cursor-pointer bg-transparent hover:underline"
                      onClick={() => setSelectedEndpoints(new Set())}
                    >
                      <p>Deselect All</p>
                    </button>
                  ) : (
                    <button
                      className="cursor-pointer bg-transparent hover:underline"
                      onClick={() => setSelectedEndpoints(new Set(edrIds))}
                    >
                      <p>Select All</p>
                    </button>
                  ))}
              </>
            )}
          </div>
        )}
      </header>

      <div className="flex flex-1 flex-col overflow-y-auto">
        {partnerEndpointsQuery.isLoading || deploy.isPending ? (
          <div className="flex h-12 items-center justify-center">
            <Loading />
          </div>
        ) : (
          <>
            {view === "list" && (
              <>
                {edrIds.map((id) => (
                  <div
                    className="flex gap-2 border-b border-stroke-line p-4"
                    key={id}
                  >
                    <Checkbox
                      checked={selectedEndpoints.has(id)}
                      onCheckedChange={(e) => {
                        if (e === true) {
                          setSelectedEndpoints(
                            new Set(selectedEndpoints.add(id)),
                          );
                        } else {
                          selectedEndpoints.delete(id);
                          setSelectedEndpoints(new Set(selectedEndpoints));
                        }
                      }}
                    />
                    <p className="m-0">
                      {partnerEndpoints[id]?.hostname ?? "Unknown"}
                    </p>
                    {endpointIds?.has(id) && (
                      <div className="ml-auto">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <CheckmarkIcon className="text-status-green" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent align="end">
                              Deployed <TooltipArrow />
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                ))}

                {partnerEndpointsQuery.hasNextPage && (
                  <Button
                    loading={partnerEndpointsQuery.isFetchingNextPage}
                    className="mx-auto my-4"
                    onClick={() => partnerEndpointsQuery.fetchNextPage()}
                    intent="primary"
                  >
                    Load More
                  </Button>
                )}
              </>
            )}

            {view === "deploy" && (
              <div className="flex flex-col items-center justify-center gap-2 pt-4">
                <CheckmarkIcon className="size-8 text-status-green" />
                <p className="m-0 text-secondary-foreground">
                  Deployment request sent for {selectedEndpoints.size}{" "}
                  {pluralize(selectedEndpoints.size, "probe")}
                </p>
              </div>
            )}
          </>
        )}
      </div>
      <footer className="flex items-center justify-between border-t border-stroke-line p-2">
        {view === "list" && (
          <>
            <Button
              intent="secondary"
              disabled={
                partnerEndpointsQuery.isLoading ||
                selectedEndpoints.size === 0 ||
                deploy.isPending
              }
              onClick={() => setSelectedEndpoints(new Set())}
            >
              Cancel
            </Button>
            <Button
              intent="primary"
              disabled={
                partnerEndpointsQuery.isLoading ||
                selectedEndpoints.size === 0 ||
                deploy.isPending
              }
              onClick={handleDeploy}
              loading={deploy.isPending}
            >
              {getDeployButtonCopy()}
            </Button>
          </>
        )}

        {view === "deploy" && (
          <Button
            intent="secondary"
            onClick={() => {
              setView("list");
              setSelectedEndpoints(new Set());
            }}
          >
            Back to list
          </Button>
        )}
      </footer>
    </DefaultOverlay>
  );
}
